/**
 * RNRB Stage 1 — residence facts (residence-history model: Slice 2 engine, activated by Slices 3+4).
 *
 * Contract: legal/reports/rnrb-residence-history-decisions.md (FIVE_DECISIONS_LOCKED — SLICE_2_READY).
 * Authorities: AUTH-IHTA1984-S8H Q1-Q3 (s.8H(2) residence at a time while the estate held an interest; one QRI;
 * nomination), AUTH-IHTA1984-S8FA-S8FD Q7 (s.8FB condition G: no residential property interest at death).
 *
 * Stage 1 decides whether there is a residential property interest (RPI), the one qualifying residential interest
 * (QRI), whether a nomination is needed, and so s.8FA vs s.8FB. It never reads who inherits a property (Stage 2).
 *
 * Mode is set only by iht_details.residence_facts_version (§13):
 *   === 2   new mode: properties[].has_been_residence and residence_bands.nomination_status are authoritative;
 *           is_main_residence is never read as evidence (a v2 null never falls back to a legacy true, §9).
 *   absent  legacy mode: LEGACY_RESIDENCE_POLICY decides. 'strict' (policy B, active from the Slice 3/4 atomic
 *           intake switch) routes legacy estates through the same Stage-1 model with provisional answers and the same
 *           condition-G gate. 'compat' (Slice 2 only) kept the pre-activation is_main_residence code paths.
 * Nothing here infers mode from field presence, property count or UI state.
 */

export type LegacyResidencePolicy = 'compat' | 'strict';

/** Policy B for every unstamped estate (§2, §3), activated with the Slice 3/4 intake switch (§11). Slice 2 had 'compat'. */
export const LEGACY_RESIDENCE_POLICY: LegacyResidencePolicy = 'strict';

/** The only stamp value that selects the new engine. Any other value is legacy (the deep schema rejects them). */
export const RESIDENCE_FACTS_V2 = 2;

export const isResidenceFactsV2 = (version: unknown): boolean => version === RESIDENCE_FACTS_V2;

/** Stage 1 runs for v2 estates, and for legacy estates only under the strict policy. */
export const residenceStage1Active = (version: unknown, policy: LegacyResidencePolicy = LEGACY_RESIDENCE_POLICY): boolean =>
    isResidenceFactsV2(version) || policy === 'strict';

export type ResidenceAnswer = 'yes' | 'no' | 'unknown';

/** 'yes' / 'no' are established facts; 'unsure', null, absent and anything outside the enum are unknown (§4, §9). */
export const readHasBeenResidence = (v: unknown): ResidenceAnswer => (v === 'yes' ? 'yes' : v === 'no' ? 'no' : 'unknown');

export type NominationStatus = 'nominated' | 'undecided' | null;
export type ResidenceBands = { qualifying_property_id?: string | null; nomination_status?: NominationStatus | string } | null | undefined;

export type PropertyLike = {
    id?: string;
    has_been_residence?: unknown;
    is_main_residence?: boolean | null;
    given_away_in_life?: boolean | null;
    gift_with_reservation?: unknown;
    [k: string]: unknown;
};

/** Lifetime-gift rule S1: a property given away in life is not an ordinary current RPI; it feeds condition G. */
export const isLifetimeGift = (p: PropertyLike): boolean => p.given_away_in_life === true;

/**
 * D36 (decision record Amendment A3). A lifetime gift whose reservation of benefit is not ruled out (any answer but an
 * explicit 'no') may still be in the estate at death (FA 1986 s.102(3)), and if it has been the deceased's residence it
 * can be a residential property interest in a second dwelling-house (s.8H(2)), so the PRs may have to nominate (s.8H(4)).
 * It is not modelled as a QRI (S1 unchanged: never counted as an ordinary RPI, never nominable, never valued); it only
 * stops one ordinary home being taken as the sole QRI.
 *   residential — has_been_residence 'yes': lived in; in the estate if the reservation is 'yes', possibly otherwise.
 *   unknown     — has_been_residence not answered 'yes' or 'no'.
 * A gift answered 'no' to the residence question, or with a reservation answered 'no', is not a candidate.
 */
export const reservedBenefitCandidates = (properties: PropertyLike[]): { residential: PropertyLike[]; unknown: PropertyLike[] } => {
    const open = properties.filter((p) => isLifetimeGift(p) && p.gift_with_reservation !== 'no');
    return {
        residential: open.filter((p) => readHasBeenResidence(p.has_been_residence) === 'yes'),
        unknown: open.filter((p) => readHasBeenResidence(p.has_been_residence) === 'unknown'),
    };
};

/**
 * D36 (decision record Amendment A5). A home given away in life whose reservation of benefit is answered 'yes' and which
 * has been the deceased's residence ('yes') is in the estate at death (FA 1986 s.102(3): AUTH-IHTM46012, the estate on
 * death includes gift-with-reservation property) and is a residential property interest (s.8H(2)), so it
 * can be the QRI (AUTH-IHTM46014: "a home that is a GWR can be a 'qualifying residential interest'"): the sole QRI where
 * it is the only residential interest (s.8H(3)), or a candidate the PRs may nominate (s.8H(4)). Any other lifetime gift
 * keeps the A3 treatment: one whose reservation is not answered (or 'unsure') may be in the estate, so it is a candidate
 * that blocks a sole QRI but is never itself nominated or valued. Version 2 only: an unstamped estate's residence answers
 * are not read (§2), so a gift there keeps rule S1.
 */
export const isReservedBenefitRpi = (p: PropertyLike): boolean =>
    isLifetimeGift(p) && p.gift_with_reservation === 'yes' && readHasBeenResidence(p.has_been_residence) === 'yes';

/**
 * D36 partial gifts (Amendment A5 correction). The QRI is "the person's interests in that dwelling-house" (s.8H(3), (4);
 * AUTH-IHTA1984-S8H Q2, Q3), all combined into one (AUTH-IHTM46011 Q3) — not one property record. A reserved-benefit
 * gift of part of a home (deceased_share_percent above 0 and below 100) may sit beside another interest the deceased kept
 * in the same home. Whether it does is never inferred (not from labels, and not from the share: the rest may have been
 * someone else's); the gift record states it:
 *   gift_retained_interest 'none'     — they kept no other interest in it: the gift record is the whole interest.
 *   gift_retained_interest 'recorded' — the interest they kept is the property gift_same_home_property_id (an ordinary
 *                                       record): the two are one home, one candidate, valued together.
 *   'unsure', null / absent, or a link that does not resolve — unresolved: the RNRB is withheld (qri_same_dwelling).
 * A whole gift (100%) needs no answer. A linked home is unresolved too where the shares exceed 100%, the two records give
 * the home different values, the kept share is not recorded, or the residence answers contradict each other.
 */
export const readGiftRetainedInterest = (v: unknown): 'recorded' | 'none' | 'unsure' | null =>
    (v === 'recorded' || v === 'none' || v === 'unsure' ? v : null);

/** Whether a reserved-benefit gift is of part of a home (so the same-home question applies). */
export const isPartialReservedBenefitGift = (p: PropertyLike): boolean => {
    const share = Number(p.deceased_share_percent);
    return isReservedBenefitRpi(p) && share > 0 && share < 100;
};

/** Homes made of a kept ordinary interest plus reserved-benefit gifts of the rest (rep → gifts), and unresolved gifts. */
export const reservedBenefitDwellings = (properties: PropertyLike[]): { groups: Map<PropertyLike, PropertyLike[]>; unresolved: PropertyLike[] } => {
    const groups = new Map<PropertyLike, PropertyLike[]>();
    const unresolved: PropertyLike[] = [];
    for (const g of properties.filter(isPartialReservedBenefitGift)) {
        const rel = readGiftRetainedInterest(g.gift_retained_interest);
        if (rel === 'none') continue;
        const id = g.gift_same_home_property_id;
        const home = rel === 'recorded' && typeof id === 'string' && id !== '' ? properties.filter((x) => x.id === id && x !== g) : [];
        if (home.length !== 1 || isLifetimeGift(home[0])) { unresolved.push(g); continue; }
        groups.set(home[0], [...(groups.get(home[0]) ?? []), g]);
    }
    for (const [home, gifts] of [...groups]) {
        const members = [home, ...gifts];
        const total = members.reduce((sum, p) => sum + (Number(p.deceased_share_percent) || 0), 0);
        const keptShare = Number(home.deceased_share_percent) > 0;
        const sameValue = gifts.every((g) => Number(g.estimated_value) === Number(home.estimated_value));
        const contradictory = readHasBeenResidence(home.has_been_residence) === 'no'; // every member gift is lived in ('yes')
        if (!keptShare || total > 100 || !sameValue || contradictory) { unresolved.push(...gifts); groups.delete(home); }
    }
    return { groups, unresolved };
};

/**
 * Why a required nomination is not usable (§6):
 *   not_asked — nomination_status null/absent (an ID without a status is ignored)
 *   undecided — nomination_status 'undecided' (the stored ID is ignored)
 *   invalid   — 'nominated', but the ID is missing, not in properties[], not an explicit 'yes', or a lifetime gift that
 *               is not a reserved-benefit residential interest (isReservedBenefitRpi; D36, Amendment A5)
 */
export type NominationGap = 'not_asked' | 'undecided' | 'invalid';

/**
 * Typed Stage-1 residence state. Feeds the existing RNRB engine (computeIHT) — it is not a second calculator.
 *   no_rpi              — no ordinary current RPI: every current non-lifetime-gift property is an explicit 'no', or
 *                         there are none. s.8FB is possible, but only through the condition-G gate.
 *   qri                 — the one QRI is fixed: 'single_rpi' (one explicit yes, every other current property an
 *                         explicit no; since A5 possibly a reserved-benefit residential interest) or 'nominated' (a valid nomination of an explicit yes; NR1 when other
 *                         properties are unknown). provisional = a legacy strict-policy answer (§2), never v2.
 *   nomination_required — two or more RPIs and no valid nomination (qri_nomination missing). Since D36 (Amendment A3)
 *                         also one ordinary RPI plus a reserved-benefit gift that has been a residence; such a gift is
 *                         listed in rpis, and since Amendment A5 it is a valid nomination and can be the QRI where its
 *                         reservation is answered 'yes' (isReservedBenefitRpi), otherwise never.
 *   residence_unknown   — an unknown residence history that NR1 does not resolve (residence_history missing), including
 *                         (D36) a reserved-benefit gift whose residence history is unknown beside one ordinary RPI.
 *   dwelling_unknown    — (D36 partial gifts) a reserved-benefit gift of part of a home whose relation to any other
 *                         interest the deceased kept in that home is not settled (qri_same_dwelling missing). Checked first:
 *                         the candidates themselves are not known until it is.
 * A qri whose home is a kept interest plus reserved-benefit gifts of the rest carries them all in `members` (the kept
 * ordinary record is `qri`, the home's identity for nomination and display).
 */
export type ResidenceStage1 =
    | { state: 'no_rpi' }
    | { state: 'qri'; qri: PropertyLike; basis: 'single_rpi' | 'nominated'; provisional: boolean; members?: PropertyLike[] }
    | { state: 'dwelling_unknown'; unresolved: PropertyLike[] }
    | { state: 'nomination_required'; rpis: PropertyLike[]; nomination: NominationGap }
    | { state: 'residence_unknown'; unknown: PropertyLike[]; rpis: PropertyLike[] };

/** The nominated property if the nomination is valid in new mode (§6), otherwise the reason it is not usable. */
export const validNomination = (properties: PropertyLike[], bands: ResidenceBands): { property: PropertyLike } | { gap: NominationGap } => {
    const status = bands?.nomination_status ?? null;
    if (status == null) return { gap: 'not_asked' };
    if (status !== 'nominated') return { gap: 'undecided' };
    const id = bands?.qualifying_property_id;
    if (typeof id !== 'string' || id === '') return { gap: 'invalid' };
    let p = properties.find((x) => x.id === id);
    // D36 partial gifts: a nomination names a dwelling — the id of a gift linked to its home stands for that home.
    const { groups } = reservedBenefitDwellings(properties);
    for (const [home, gifts] of groups) if (p && gifts.includes(p)) p = home;
    const lived = p && groups.has(p) ? 'yes' : readHasBeenResidence(p?.has_been_residence);
    if (!p || lived !== 'yes' || (isLifetimeGift(p) && !isReservedBenefitRpi(p))) return { gap: 'invalid' };
    return { property: p };
};

/** New mode (residence_facts_version === 2). Reads only has_been_residence and the nomination fields. */
export const resolveResidenceStage1V2 = (properties: PropertyLike[], bands: ResidenceBands): ResidenceStage1 => {
    // D36 partial gifts: which records are one home is settled first — the candidates depend on it (no nomination can).
    const dwellings = reservedBenefitDwellings(properties);
    if (dwellings.unresolved.length > 0) return { state: 'dwelling_unknown', unresolved: dwellings.unresolved };
    // A home made of a kept interest and gifts of the rest is one unit, represented by the kept record; it has been the
    // residence (each member gift is lived in, and a contradicting 'no' on the kept record is unresolved above).
    const linked = new Set<PropertyLike>([...dwellings.groups.values()].flat());
    const units = properties.filter((p) => !linked.has(p));
    const lived = (p: PropertyLike): ResidenceAnswer => (dwellings.groups.has(p) ? 'yes' : readHasBeenResidence(p.has_been_residence));
    const withMembers = (s: ResidenceStage1): ResidenceStage1 =>
        (s.state === 'qri' && dwellings.groups.has(s.qri) ? { ...s, members: [s.qri, ...dwellings.groups.get(s.qri)!] } : s);
    const current = units.filter((p) => !isLifetimeGift(p));
    const rpis = current.filter((p) => lived(p) === 'yes');
    const unknown = current.filter((p) => lived(p) === 'unknown');
    const nom = validNomination(properties, bands);
    // NR1 (§8): a valid nomination of an explicit yes fixes the QRI and the s.8FA path, whatever the other properties.
    if ('property' in nom) return withMembers({ state: 'qri', qri: nom.property, basis: 'nominated', provisional: false });
    // D36 (Amendment A5): a reserved-benefit residential interest counts exactly as an ordinary explicit-yes RPI; the rest
    // of the resolution (A3 candidates, unknowns) is unchanged. Estates without one resolve exactly as before.
    if (units.some(isReservedBenefitRpi)) return withMembers(resolveWithRpis([...rpis, ...units.filter(isReservedBenefitRpi)], unknown, units, nom.gap));
    return withMembers(resolveWithRpis(rpis, unknown, units, nom.gap));
};

/** The A3 resolution once the RPIs (and unknown current properties) are known and no nomination is valid. */
const resolveWithRpis = (rpis: PropertyLike[], unknown: PropertyLike[], properties: PropertyLike[], gap: NominationGap): ResidenceStage1 => {
    if (rpis.length >= 2) return { state: 'nomination_required', rpis, nomination: gap };
    if (rpis.length === 1 && unknown.length === 0) {
        // D36: one ordinary home is the sole QRI only if no reserved-benefit gift may be a second residential interest.
        const reserved = reservedBenefitCandidates(properties);
        const others = reserved.residential.filter((p) => !rpis.includes(p));
        if (others.length > 0) return { state: 'nomination_required', rpis: [...rpis, ...others], nomination: gap };
        if (reserved.unknown.length > 0) return { state: 'residence_unknown', unknown: reserved.unknown, rpis };
        return { state: 'qri', qri: rpis[0], basis: 'single_rpi', provisional: false };
    }
    if (unknown.length > 0) return { state: 'residence_unknown', unknown, rpis };
    return { state: 'no_rpi' };
};

/**
 * Legacy strict policy B (§2), for unstamped estates while LEGACY_RESIDENCE_POLICY === 'strict'. Exactly one
 * is_main_residence true is a provisional yes and provisional nomination; two or more need a nomination; false and
 * null are unknown, never a statutory no. New fields on an unstamped estate are ignored. NR1 never applies here.
 */
export const resolveResidenceStage1LegacyStrict = (properties: PropertyLike[]): ResidenceStage1 => {
    const current = properties.filter((p) => !isLifetimeGift(p));
    const marked = current.filter((p) => p.is_main_residence === true);
    if (marked.length === 1) return { state: 'qri', qri: marked[0], basis: 'single_rpi', provisional: true };
    if (marked.length >= 2) return { state: 'nomination_required', rpis: marked, nomination: 'not_asked' };
    if (current.length > 0) return { state: 'residence_unknown', unknown: current, rpis: [] };
    return { state: 'no_rpi' };
};

export const resolveResidenceStage1 = (properties: PropertyLike[], version: unknown, bands: ResidenceBands): ResidenceStage1 =>
    isResidenceFactsV2(version) ? resolveResidenceStage1V2(properties, bands) : resolveResidenceStage1LegacyStrict(properties);

// ── Consumer-facing residence resolution (Slice 6) ───────────────────────────────────────────────────────────

/**
 * The Stage-1 residence state as downstream consumers (form mapping, evidence pack, risk/review flags, consistency
 * checks, report wording) read it. It is resolveResidenceStage1 (the engine's own resolver) with a stable shape, so no
 * consumer re-derives Stage 1 or reads is_main_residence itself:
 *   qri                 — the one QRI. provisional = a legacy strict-policy answer (§2 H1), never confirmed.
 *   nomination_required — two or more RPIs and no valid nomination: no property is chosen (§6).
 *   residence_unknown   — an unknown residence history that NR1 does not resolve: nothing is said either way (§9).
 *   no_rpi              — no current RPI (every current property an explicit 'no', or none): any RNRB is s.8FB only.
 * candidates are the properties that may be the QRI: the QRI itself, the unnominated RPIs, or the RPIs plus the
 * properties whose history is unknown. A legacy false/null is unknown, never a statutory no (§2 H3).
 */
export type ResidenceResolution<P extends PropertyLike = PropertyLike> = {
    mode: 'v2' | 'legacy';
    state: ResidenceStage1['state'];
    qri: P | null;
    qriId: string | null;
    provisional: boolean;
    nominationRequired: boolean;
    residenceHistoryUnknown: boolean;
    /** D36 partial gifts: whether a gift of part of a home is the same home as a kept interest is not settled. */
    sameDwellingUnknown: boolean;
    noCurrentRpi: boolean;
    candidates: P[];
};

export type ResidenceDetailsLike = { residence_facts_version?: unknown; residence_bands?: ResidenceBands; [k: string]: unknown } | null | undefined;

export const resolveResidenceForConsumers = <P extends PropertyLike>(
    properties: P[] | null | undefined,
    ihtDetails: ResidenceDetailsLike,
): ResidenceResolution<P> => {
    const s = resolveResidenceStage1(properties ?? [], ihtDetails?.residence_facts_version, ihtDetails?.residence_bands);
    const qri = s.state === 'qri' ? (s.qri as P) : null;
    const candidates = (s.state === 'qri' ? [s.qri]
        : s.state === 'nomination_required' ? s.rpis
            : s.state === 'residence_unknown' ? [...s.rpis, ...s.unknown]
                : s.state === 'dwelling_unknown' ? s.unresolved : []) as P[];
    return {
        mode: isResidenceFactsV2(ihtDetails?.residence_facts_version) ? 'v2' : 'legacy',
        state: s.state,
        qri,
        qriId: typeof qri?.id === 'string' ? qri.id : null,
        provisional: s.state === 'qri' && s.provisional,
        nominationRequired: s.state === 'nomination_required',
        residenceHistoryUnknown: s.state === 'residence_unknown' || s.state === 'dwelling_unknown',
        sameDwellingUnknown: s.state === 'dwelling_unknown',
        noCurrentRpi: s.state === 'no_rpi',
        candidates,
    };
};

// ── Condition-G screening (§5) — governs entry to s.8FB only ─────────────────────────────────────────────────

export type ConditionGScreening = 'G_CLEAR' | 'G_POSITIVE' | 'G_UNKNOWN';

export type TrustScreeningLike = {
    created_or_transferred_trust?: boolean | null;
    benefited_from_trust?: boolean | null;
    has_gift_with_reservation?: boolean | null;
    uncertain?: boolean | null;
    has_trusts?: boolean | null;
    [k: string]: unknown;
} | null | undefined;

export type TrustsOrLifeInterestLike = { exists?: boolean | null; unsure?: boolean | null; [k: string]: unknown } | null | undefined;

/**
 * Absence from properties[] never establishes condition G: reserved-benefit property and settled property with an
 * interest in possession can be in the estate at death. Positive evidence wins; clear needs every screening fact to
 * be an explicit negative; null / absent / unsure is never false. has_trusts === false never contributes to clear
 * (it is a derived alias that is false when Q1/Q2 were never answered). For a lifetime gift only an explicit GWR
 * 'no' is neutral.
 */
export const screenConditionG = (ts: TrustScreeningLike, tli: TrustsOrLifeInterestLike, properties: PropertyLike[]): ConditionGScreening => {
    const positive = ts?.created_or_transferred_trust === true
        || ts?.benefited_from_trust === true
        || ts?.has_gift_with_reservation === true
        || ts?.has_trusts === true
        || tli?.exists === true
        || properties.some((p) => p.gift_with_reservation === 'yes');
    if (positive) return 'G_POSITIVE';
    const clear = ts?.created_or_transferred_trust === false
        && ts?.benefited_from_trust === false
        && ts?.has_gift_with_reservation === false
        && ts?.uncertain !== true
        && tli?.unsure !== true
        && !properties.some((p) => p.gift_with_reservation === 'unsure')
        && !properties.some((p) => isLifetimeGift(p) && p.gift_with_reservation !== 'no');
    return clear ? 'G_CLEAR' : 'G_UNKNOWN';
};

// ── NR2: nothing closely inherited, no claim (§8) ────────────────────────────────────────────────────────────

const NR2_SOLE_ZERO_DESTINATIONS = new Set(['spouse', 'charity', 'other']);
const NR2_WILL_TRUST_ZERO = new Set(['spouse', 'other_individual', 'none_discretionary']);
const NR2_JT_ZERO = new Set(['spouse', 'other', 'charity']);

/**
 * The property's inheritance route is known and passes nothing closely inherited (§8 precondition 3), using the
 * locked route list. Any unknown route ('', null, 'unknown', 'unspecified', an unanswered survivor, a descendant
 * destination with an 'unsure'/null trust type) is not zero. An empty beneficiary_allocations [] is treated as absent,
 * so the destination governs, as in the engine (D40 resolved): a descendant destination with [] is zero only when
 * it is discretionary, exactly as without the array.
 */
export const nr2RouteIsKnownZero = (p: any): boolean => {
    const ii = p.immediate_interest;
    if (ii != null) return NR2_WILL_TRUST_ZERO.has(ii);
    if (p.ownership_type === 'joint_tenants') return NR2_JT_ZERO.has(p.joint_owner_relationship);
    const dest = p.deceased_share_passes_to;
    const allocs: Array<{ type?: string; percentage?: number }> | null | undefined = p.beneficiary_allocations;
    if (dest === 'direct_descendants') return p.descendant_inheritance_type === 'discretionary';
    if (!NR2_SOLE_ZERO_DESTINATIONS.has(dest)) return false;
    return !(allocs ?? []).some((a) => a.type === 'direct_descendant' && (Number(a.percentage) || 0) > 0);
};

/** A discretionary descendant route that NR2 counts as nothing closely inherited (existing two-year warning kept). */
export const nr2DiscretionaryRoute = (p: any): boolean =>
    p.immediate_interest === 'none_discretionary'
    || (p.immediate_interest == null && p.ownership_type !== 'joint_tenants'
        && p.deceased_share_passes_to === 'direct_descendants' && p.descendant_inheritance_type === 'discretionary');

// ── Former-home gateway (§6; Slice 5) ────────────────────────────────────────────────────────────────────────

export type FormerResidenceDisposed = 'yes' | 'no' | 'unsure';
/**
 * The former-home fact used by the engine (§6). The residence_facts_version stamp plays no part: §6 says the stamp
 * does not activate this field, so the same estate gives the same state stamped or unstamped.
 *   'yes' | 'no' | 'unsure' — an explicit answer to the former-home question: authoritative; `applies` is not read.
 *   'provisional'           — unanswered, legacy `applies === true`: a provisional yes. The entered figures are used,
 *                             and where that is material the fact is reported missing (incomplete), never confirmed.
 *   'unknown'               — unanswered, `applies` false, null or absent: unknown, never a factual 'no'. No figures used.
 */
export type FormerHomeState = FormerResidenceDisposed | 'provisional' | 'unknown';

export const readFormerResidenceDisposed = (v: unknown): FormerResidenceDisposed | null =>
    (v === 'yes' || v === 'no' || v === 'unsure' ? v : null);

/**
 * `applies` is the legacy downsizing_allowance.applies. `figuresSupplied` covers only a caller that omits `applies`
 * altogether (undefined): the derivation always passes it (true / false / null) and supplies downsizing figures for an
 * unanswered estate only under `applies === true`, so supplied figures stand for that state. An explicit false or null
 * is never overridden by figures.
 */
export const formerHomeState = (explicit: unknown, applies: unknown, figuresSupplied = false): FormerHomeState =>
    readFormerResidenceDisposed(explicit)
    ?? (applies === true || (applies === undefined && figuresSupplied) ? 'provisional' : 'unknown');

/**
 * The deceased's share (%) of the former home at completion: a finite number above 0 and at most 100. Anything else
 * (null, absent, a string, 0, negative, above 100) is unknown — never read as 100 and never rescaled (D35).
 */
export const readFormerHomeSharePercent = (v: unknown): number | null =>
    (typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= 100 ? v : null);

/**
 * The s.8FE(2) value of the former residential interest (PA-IHT-RNRB-DOWNSIZING-001 E18): the value of the interest
 * disposed of, i.e. the deceased's share of the whole former home's value at completion, rounded to the penny (D35).
 *   no whole-home value (null / not above 0) → value null, share not asked (the figures are simply missing);
 *   value and a valid share                 → value × share / 100;
 *   value but the share unknown             → value null, shareUnknown, and `wholeWithoutShare` = the whole value: an
 *                                             upper bound on the interest, never used as the interest itself.
 * A mortgage at completion is not deducted here (D37: practice-profile decision pending).
 */
export const formerHomeInterestValue = (wholeValue: unknown, sharePercent: unknown): { value: number | null; shareUnknown: boolean; wholeWithoutShare: number | null } => {
    const whole = Number(wholeValue) || 0;
    if (!(whole > 0)) return { value: null, shareUnknown: false, wholeWithoutShare: null };
    const share = readFormerHomeSharePercent(sharePercent);
    if (share === null) return { value: null, shareUnknown: true, wholeWithoutShare: whole };
    return { value: Math.round(whole * share) / 100, shareUnknown: false, wholeWithoutShare: null };
};

/**
 * The former-home answer from the downsizing question. 'no' clears the downsizing details (value, share, completion
 * date, the several-disposals list, its same-day answers and nomination, other assets to descendants, claim) so no
 * hidden stale figure survives; 'yes' and 'unsure' keep them (the engine uses them only on 'yes'). `applies` is left as
 * it is: it is read only by the fallback of an unanswered estate.
 */
export const applyFormerHomeAnswer = <D extends Record<string, any> | null | undefined>(downsizing: D, answer: FormerResidenceDisposed): Record<string, any> => ({
    ...(downsizing ?? {}),
    former_residence_disposed: answer,
    ...(answer === 'no' ? { former_property_value: null, former_property_share_percent: null, disposal_date: null, disposal_kind: null, former_residence_disposals: null, same_day_checks: null, nominated_disposal_id: null, qualifying_assets_value: null, claim: null } : {}),
});

// ── Several former-home disposals and the PRs' nomination (D42) ─────────────────────────────────────────────

/** One home on one day (types.ts FormerResidenceDisposal). Read loosely: saved data is not trusted to be well formed. */
export type FormerDisposalLike = {
    id?: unknown; label?: unknown; disposal_date?: unknown;
    former_property_value?: unknown; former_property_share_percent?: unknown; lived_in?: unknown; disposal_kind?: unknown;
};

/**
 * D36 special disposals (PA-IHT-RNRB-DOWNSIZING-001 E17; types.ts FormerDisposalKind). What the kind does to an entry:
 *   ordinary — null / absent (older saved cases, or not answered), 'sale_or_gift', and 'gift_benefit_ended' (a reservation
 *              that ended in life is a disposal at that time, s.8H(4D)(b); AUTH-IHTA1984-S8H Q12, AUTH-IHTM46056 Q1).
 *   never    — 'gift_benefit_kept' (property subject to a reservation is treated as not disposed of, s.8H(4D)(a)) and
 *              'conditionally_exempt' (it may not be, or be included in, a QFRI, s.8H(4E); AUTH-IHTA1984-S8H Q13).
 *   unknown  — 'trust_disposal' (s.8HA treats a trustees' disposal, or an interest in possession ending, as the person's
 *              disposal of the interest held by s.49(1) — a qualifying interest in possession, which is not captured or
 *              collected here) and 'unsure'. Never counted and never dropped: it may be the QFRI.
 */
export const formerDisposalKindEffect = (kind: unknown): 'ordinary' | 'never' | 'unknown' =>
    kind == null || kind === 'sale_or_gift' || kind === 'gift_benefit_ended' ? 'ordinary'
        : kind === 'gift_benefit_kept' || kind === 'conditionally_exempt' ? 'never' : 'unknown';

/** The figures the s.8FE step uses: one disposal, the same shape as the single former-home record. */
export type FormerDisposalFigures = { former_property_value: unknown; former_property_share_percent: unknown; disposal_date: unknown };

/**
 * Which disposal the downsizing addition is worked out on (PA-IHT-RNRB-DOWNSIZING-001 E15; s.8H(4A)-(4C)).
 *   legacy              — no list (absent, null or empty): the single record's fields, exactly as before D42.
 *   selected            — one entry: the only entry ('only'), the only entry that can count ('sole_candidate'), or the
 *                         PRs' valid nomination ('nominated').
 *   same_day_grouping   — two or more entries share a completion date inside the window, whatever their lived-in answers
 *                         (or one that may count has no date), and they are not confirmed as different homes. Checked
 *                         first, before residence eligibility: parts of one home disposed of on
 *                         one day are one interest (s.8H(4B)(a)(ii)), so separate entries for them are not valid
 *                         candidates and no nomination among them can be accepted.
 *   nomination_required — two or more entries can count, or one can and another may: the PRs have not validly
 *                         nominated one. Never resolved by picking an entry (first, largest or otherwise).
 *   facts_unknown       — two or more entries and none can yet be shown to count (lived-in or date unknown, or every
 *                         entry ruled out while the former-home answer says there was such a disposal). `special`: one
 *                         or more of the disposals is of a kind that never counts or is not worked out here (D36; also
 *                         the single record or a single entry of such a kind).
 */
export type FormerDisposalResolution =
    | { kind: 'legacy' }
    | { kind: 'selected'; disposal: FormerDisposalLike; via: 'only' | 'sole_candidate' | 'nominated' }
    | { kind: 'same_day_grouping'; groups: FormerDisposalSameDayGroup[]; undated: FormerDisposalLike[] }
    | { kind: 'nomination_required'; choices: FormerDisposalLike[] }
    | { kind: 'facts_unknown'; special?: boolean };

const isIsoDate = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

/**
 * Can this entry be the qualifying former residential interest? s.8H(4A)(a): a disposal of a residential property
 * interest on or after 8 July 2015 and before death; s.8H(2), (4B): the home had been the person's residence before the
 * disposal (a post-occupation time). 'yes' only on an explicit lived-in 'yes' and a completion date in that window;
 * 'no' on an explicit lived-in 'no' or a date outside it; otherwise 'unknown' (never read as 'no'). A missing value or
 * share does not decide whether the entry counts: it is a missing figure, reported by the s.8FE step once nominated.
 */
export const formerDisposalCounts = (d: FormerDisposalLike, dateOfDeath: unknown): 'yes' | 'no' | 'unknown' => {
    const lived = d.lived_in === 'yes' || d.lived_in === 'no' ? d.lived_in : 'unknown';
    const date = isIsoDate(d.disposal_date) ? d.disposal_date : null;
    const kind = formerDisposalKindEffect(d.disposal_kind);
    if (lived === 'no' || disposalDateOutsideWindow(d, dateOfDeath) || kind === 'never') return 'no';
    if (kind === 'unknown') return 'unknown';
    if (lived === 'yes' && date !== null) return 'yes';
    return 'unknown';
};

/** A completion date entered and outside s.8H(4A)(a)'s window (before 8 July 2015, or after the death). Undated: false. */
const disposalDateOutsideWindow = (d: FormerDisposalLike, dateOfDeath: unknown): boolean => {
    const date = isIsoDate(d.disposal_date) ? d.disposal_date : null;
    const dod = isIsoDate(dateOfDeath) ? dateOfDeath : null;
    return date !== null && (date < '2015-07-08' || (dod !== null && date > dod));
};

/** The saved list, or null where the single former-home record governs (absent, null, not an array or empty). */
export const readFormerDisposals = (downsizing: { former_residence_disposals?: unknown } | null | undefined): FormerDisposalLike[] | null => {
    const list = downsizing?.former_residence_disposals;
    return Array.isArray(list) && list.length > 0 ? list.filter((d) => d && typeof d === 'object') as FormerDisposalLike[] : null;
};

// ── Same-day disposals of one home (D42; s.8H(4B)(a)(ii); AUTH-IHTA1984-S8H Q10; AUTH-IHTM46053 Q2-Q3) ───────────
// Interests in the nominated home disposed of on the same day are together one qualifying former residential interest;
// different homes, or different days, are never aggregated. An entry is one home on one day, but its label is free text
// and there is no home identifier, so two entries with the same completion date may be parts of one home entered
// separately. Nothing is inferred from labels and nothing is merged: the user is asked whether they are the same home.

/** The user's answer for one set of same-day entries, keyed by exactly those entries' ids (types.ts FormerDisposalSameDayCheck). */
export type FormerDisposalSameDayCheckLike = { disposal_ids?: unknown; same_home?: unknown };

/** Two or more in-window entries with the same completion date, and the answer recorded for exactly that set. */
export type FormerDisposalSameDayGroup = { date: string; entries: FormerDisposalLike[]; ids: string[] | null; same_home: 'yes' | 'no' | 'unsure' | null };

/** The ids of a set of entries, sorted, or null where any id is missing, empty or repeated (an answer cannot be tied to it). */
const sameDayIds = (entries: FormerDisposalLike[]): string[] | null => {
    const ids = entries.map((d) => d.id);
    if (!ids.every((id): id is string => typeof id === 'string' && id !== '')) return null;
    return new Set(ids).size === ids.length ? [...ids].sort() : null;
};
const sameIdSet = (a: unknown, ids: string[]): boolean =>
    Array.isArray(a) && a.length === ids.length && [...a].sort().every((id, i) => id === ids[i]);

/**
 * Same-day groups among dated entries inside the window (on or after 8 July 2015, not after death), whatever their
 * lived-in answer: grouping comes before residence eligibility. Parts of one home disposed of on one day share that
 * home's residence history (a post-occupation time is fixed by the dwelling, s.8H(4B)), so a lived-in 'yes' / 'no'
 * contradiction cannot be used to drop one part before it is known whether they are the same home. Also: entries that
 * may count but have no date (they may share any candidate's day); undated entries explicitly not lived in are not
 * treated so. A group's answer is the check recorded for exactly its entries: after any change of date, entry or
 * removal the set differs and the question is asked again. Residence eligibility (formerDisposalCounts) applies only
 * after the groups are settled as different homes.
 * `unresolved`: an undated entry beside another that may count, or a group not answered 'no' (different homes).
 */
export const formerDisposalSameDay = (
    downsizing: { former_residence_disposals?: unknown; same_day_checks?: unknown } | null | undefined,
    dateOfDeath: unknown,
): { groups: FormerDisposalSameDayGroup[]; undated: FormerDisposalLike[]; unresolved: boolean } => {
    const list = readFormerDisposals(downsizing);
    if (!list || list.length < 2) return { groups: [], undated: [], unresolved: false };
    const mayCount = list.filter((d) => formerDisposalCounts(d, dateOfDeath) !== 'no');
    const checks = Array.isArray(downsizing?.same_day_checks)
        ? (downsizing!.same_day_checks as unknown[]).filter((c): c is FormerDisposalSameDayCheckLike => !!c && typeof c === 'object') : [];
    const byDate = new Map<string, FormerDisposalLike[]>();
    for (const d of list) {
        // D36: a disposal that is not one (s.8H(4D)(a)) or can never be included in a QFRI (s.8H(4E)) is no part of a day's interest.
        if (isIsoDate(d.disposal_date) && !disposalDateOutsideWindow(d, dateOfDeath) && formerDisposalKindEffect(d.disposal_kind) !== 'never') {
            byDate.set(d.disposal_date, [...(byDate.get(d.disposal_date) ?? []), d]);
        }
    }
    const groups: FormerDisposalSameDayGroup[] = [...byDate].filter(([, entries]) => entries.length >= 2).map(([date, entries]) => {
        const ids = sameDayIds(entries);
        const matches = ids ? checks.filter((c) => sameIdSet(c.disposal_ids, ids)) : [];
        const answer = matches.length === 1 ? matches[0].same_home : null;
        return { date, entries, ids, same_home: answer === 'yes' || answer === 'no' || answer === 'unsure' ? answer : null };
    });
    const undated = mayCount.length >= 2 ? mayCount.filter((d) => !isIsoDate(d.disposal_date)) : [];
    return { groups, undated, unresolved: undated.length > 0 || groups.some((g) => g.same_home !== 'no') };
};

/**
 * Resolves the disposal for the s.8FE step (D42). Base rule: the number of entries that can count, not the number of
 * entries. A single entry is the only disposal recorded, so it is used as the single record always was (the former-home
 * answer already says it was a home they lived in; the s.8FE step still tests its date), unless it is explicitly not
 * lived in. With two or more: first, entries that may be parts of one home on one day must be settled (same-day
 * grouping); then a valid nomination (an id held by exactly one entry, and that entry counts) fixes it, as the PRs'
 * nomination does in law; otherwise exactly one entry that counts with none unknown is the only possible interest and
 * needs no nomination; otherwise the nomination (or the facts) are missing.
 */
export const resolveFormerDisposal = (
    downsizing: { former_residence_disposals?: unknown; nominated_disposal_id?: unknown; same_day_checks?: unknown; disposal_kind?: unknown } | null | undefined,
    dateOfDeath: unknown,
): FormerDisposalResolution => {
    const list = readFormerDisposals(downsizing);
    // D36: the single record, or a single entry, of a kind that never counts or is not worked out here fixes no disposal.
    if (!list) return formerDisposalKindEffect(downsizing?.disposal_kind) === 'ordinary' ? { kind: 'legacy' } : { kind: 'facts_unknown', special: true };
    if (list.length === 1) {
        if (formerDisposalKindEffect(list[0].disposal_kind) !== 'ordinary') return { kind: 'facts_unknown', special: true };
        return list[0].lived_in === 'no' ? { kind: 'facts_unknown' } : { kind: 'selected', disposal: list[0], via: 'only' };
    }
    // Same-day grouping before any selection: a nomination of one part of a home cannot stand for that day's interest.
    const sameDay = formerDisposalSameDay(downsizing, dateOfDeath);
    if (sameDay.unresolved) {
        return { kind: 'same_day_grouping', groups: sameDay.groups.filter((g) => g.same_home !== 'no'), undated: sameDay.undated };
    }
    const { choices, unknown, nominated, nominationInvalid } = formerDisposalCandidates(list, downsizing?.nominated_disposal_id, dateOfDeath);
    if (nominated) return { kind: 'selected', disposal: nominated, via: 'nominated' };
    // A stated nomination that does not resolve (unknown or duplicated id, or an entry that does not count) is never
    // replaced by another entry, even the only one that counts: the PRs' choice is re-asked, not overridden.
    if (choices.length === 1 && unknown === 0 && !nominationInvalid) return { kind: 'selected', disposal: choices[0], via: 'sole_candidate' };
    if (choices.length >= 1) return { kind: 'nomination_required', choices };
    return { kind: 'facts_unknown', special: list.some((d) => formerDisposalKindEffect(d.disposal_kind) !== 'ordinary') };
};

/** The entries that count, how many may, and the nomination's validity (an id held by exactly one entry that counts). */
const formerDisposalCandidates = (list: FormerDisposalLike[], nominatedId: unknown, dateOfDeath: unknown) => {
    const counts = list.map((d) => formerDisposalCounts(d, dateOfDeath));
    const choices = list.filter((_, i) => counts[i] === 'yes');
    const unknown = counts.filter((c) => c === 'unknown').length;
    const stated = typeof nominatedId === 'string' && nominatedId !== '';
    const matches = stated ? list.filter((d) => d.id === nominatedId) : [];
    const nominated = matches.length === 1 && formerDisposalCounts(matches[0], dateOfDeath) === 'yes' ? matches[0] : null;
    return { choices, unknown, nominated, nominationInvalid: stated && !nominated };
};

/** The figures for the s.8FE step, or null where no disposal is fixed (D42). Legacy: the single record's fields. */
export const formerDisposalFigures = (
    downsizing: (FormerDisposalFigures & { former_residence_disposals?: unknown; nominated_disposal_id?: unknown }) | null | undefined,
    resolution: FormerDisposalResolution,
): FormerDisposalFigures | null => {
    if (resolution.kind === 'legacy') {
        return { former_property_value: downsizing?.former_property_value, former_property_share_percent: downsizing?.former_property_share_percent, disposal_date: downsizing?.disposal_date };
    }
    if (resolution.kind !== 'selected') return null;
    const d = resolution.disposal;
    return { former_property_value: d.former_property_value, former_property_share_percent: d.former_property_share_percent, disposal_date: d.disposal_date };
};

/**
 * Stale former-home state clearing (D42), as reconcileNomination does for the QRI. Same-day answers are kept only for a
 * set of entries that still shares a date (a changed date, entry or removal leaves none behind). A nomination whose entry
 * was removed, is duplicated, no longer counts, or is in a same-day set not answered as different homes is cleared to
 * null. Used by the form after each edit; the runtime still validates both independently (resolveFormerDisposal).
 */
export const reconcileFormerNomination = <D extends { former_residence_disposals?: unknown; nominated_disposal_id?: unknown; same_day_checks?: unknown }>(downsizing: D, dateOfDeath: unknown): D => {
    let next = downsizing;
    const list = readFormerDisposals(downsizing);
    const checks = downsizing?.same_day_checks;
    if (checks !== null && checks !== undefined) {
        const groupIds = formerDisposalSameDay(downsizing, dateOfDeath).groups.map((g) => g.ids).filter((ids): ids is string[] => !!ids);
        const kept = Array.isArray(checks)
            ? (checks as FormerDisposalSameDayCheckLike[]).filter((c) => !!c && typeof c === 'object' && groupIds.some((ids) => sameIdSet(c.disposal_ids, ids)))
            : [];
        if (!Array.isArray(checks) || kept.length !== checks.length) next = { ...next, same_day_checks: kept.length ? kept : null };
    }
    const id = next?.nominated_disposal_id;
    if (typeof id !== 'string' || id === '') return next;
    const unresolvedIds = new Set(formerDisposalSameDay(next, dateOfDeath).groups.filter((g) => g.same_home !== 'no').flatMap((g) => g.entries.map((d) => d.id)));
    return list && formerDisposalCandidates(list, id, dateOfDeath).nominated && !unresolvedIds.has(id) ? next : { ...next, nominated_disposal_id: null };
};

/**
 * When the former-home nomination question is asked (D42): two or more entries, same-day grouping settled, and at least
 * two that count, or one that counts and one that may, or one that counts beside a stated nomination that does not
 * resolve. Only entries that count are offered. While same-day grouping is unsettled the question is not asked: the
 * grouping question comes first and a nomination cannot settle it. Mirrors resolveFormerDisposal, which is the authority.
 */
export const formerDisposalNominationPrompt = (
    downsizing: { former_residence_disposals?: unknown; nominated_disposal_id?: unknown; same_day_checks?: unknown } | null | undefined,
    dateOfDeath: unknown,
): { show: boolean; choices: FormerDisposalLike[] } => {
    const list = readFormerDisposals(downsizing);
    if (!list || list.length < 2) return { show: false, choices: [] };
    if (formerDisposalSameDay(downsizing, dateOfDeath).unresolved) return { show: false, choices: [] };
    const { choices, unknown, nominationInvalid } = formerDisposalCandidates(list, downsizing?.nominated_disposal_id, dateOfDeath);
    return { show: choices.length >= 2 || (choices.length >= 1 && (unknown >= 1 || nominationInvalid)), choices };
};

export type Nr2Facts = {
    properties: PropertyLike[];
    screening: ConditionGScreening;
    downsizingClaim: 'yes' | 'no' | 'unsure' | null | undefined;
    /** Any downsizing data: downsizing_allowance.applies === true, or a former value / disposal date / VT entered. */
    downsizingData: boolean;
    /** The former-home fact (Slice 5). Omitted (Slice-2 callers only; the engine always passes it) = the pre-Slice-5 rule. */
    formerHome?: FormerHomeState;
};

/**
 * NR2 (§8): RNRB is a confirmed £0 despite residence-history or nomination unknowns only if all hold:
 *   1. no downsizing claim in play;
 *   2. screening G_CLEAR;
 *   3-4. every non-lifetime-gift property has a known route with a zero closely-inherited share.
 * Precondition 1 with the Slice-5 contract update: former home 'no' → no claim in play; 'yes', 'unsure', a provisional
 * yes (applies === true) or unknown (applies false / null / absent) → in play unless the downsizing claim is an explicit
 * 'no' (only then is the former-home fact immaterial). Identical stamped or unstamped. A caller that omits formerHome
 * (Slice-2 unit use) keeps the pre-Slice-5 rule (claim 'no', or no downsizing data and no claim answer).
 */
export const nr2ConfirmedZero = (f: Nr2Facts): boolean => {
    const fh = f.formerHome;
    const noClaimInPlay = fh === 'no' ? true
        : fh === undefined ? (f.downsizingClaim === 'no' || (f.downsizingClaim == null && !f.downsizingData))
            : f.downsizingClaim === 'no';
    if (!noClaimInPlay) return false;
    if (f.screening !== 'G_CLEAR') return false;
    return f.properties.filter((p) => !isLifetimeGift(p)).every(nr2RouteIsKnownZero);
};

// ── Compatibility mirror (§14; retired in Slice 6) and intake state (Slices 3+4) ─────────────────────────────

/**
 * @deprecated Retired from active use in Slice 6: no production code writes or reads the mirror. Every consumer reads
 * resolveResidenceForConsumers, and syncResidenceContext no longer rewrites is_main_residence on a v2 estate, so a
 * stored v2 value may be stale and is meaningless. Kept only as the record of the §14 derivation (tests).
 * The derivation: true on exactly the resolved QRI (one explicit yes with every other current property an explicit no,
 * or a valid nomination of an explicit yes), false on every property otherwise.
 */
export const deriveResidenceMirror = (properties: PropertyLike[], bands: ResidenceBands): Map<PropertyLike, boolean> => {
    const s = resolveResidenceStage1V2(properties, bands);
    return new Map(properties.map((p) => [p, s.state === 'qri' && s.qri === p]));
};

/** @deprecated Retired in Slice 6 (see deriveResidenceMirror). The mirror written onto the properties. */
export const applyResidenceMirror = <P extends PropertyLike>(properties: P[], bands: ResidenceBands): P[] => {
    const mirror = deriveResidenceMirror(properties, bands);
    let changed = false;
    const out = properties.map((p) => {
        const want = mirror.get(p) === true;
        if (p.is_main_residence === want) return p;
        changed = true;
        return { ...p, is_main_residence: want };
    });
    return changed ? out : properties;
};

/**
 * Stale-nomination clearing (§6): a 'nominated' status whose property was deleted, is no longer an explicit yes, or
 * is now a lifetime gift is cleared (status and ID both null). 'undecided' and null are kept. The engine still
 * validates a nomination independently (validNomination).
 */
export const reconcileNomination = (properties: PropertyLike[], bands: ResidenceBands): ResidenceBands => {
    if (bands?.nomination_status !== 'nominated') return bands;
    return 'property' in validNomination(properties, bands) ? bands : { ...bands, nomination_status: null, qualifying_property_id: null };
};

/**
 * When the nomination question is asked (§6): two or more explicit yes, or at least one explicit yes and at least one
 * 'unsure' or unanswered, among current (non-lifetime-gift) properties; or (D36, Amendment A3) at least one explicit yes
 * and a reserved-benefit gift that is or may be a second residential interest. Offered: explicit-yes current properties
 * and (D36, Amendment A5) reserved-benefit residential interests (isReservedBenefitRpi); any other lifetime gift is never
 * a valid nomination (§6).
 */
export const nominationPrompt = <P extends PropertyLike>(properties: P[]): { show: boolean; choices: P[] } => {
    // D36 partial gifts: the same-home question comes first; a home of a kept interest and gifts of the rest is one choice.
    const dwellings = reservedBenefitDwellings(properties);
    if (dwellings.unresolved.length > 0) return { show: false, choices: [] };
    const linked = new Set<PropertyLike>([...dwellings.groups.values()].flat());
    const units = properties.filter((p) => !linked.has(p));
    const lived = (p: P): ResidenceAnswer => (dwellings.groups.has(p) ? 'yes' : readHasBeenResidence(p.has_been_residence));
    const current = units.filter((p) => !isLifetimeGift(p));
    // D36 (Amendment A5): a reserved-benefit residential interest is offered beside the ordinary explicit-yes homes.
    const choices = [...current.filter((p) => lived(p) === 'yes'), ...units.filter(isReservedBenefitRpi)];
    const unknown = current.filter((p) => lived(p) === 'unknown');
    const reserved = reservedBenefitCandidates(units);
    const reservedCount = reserved.residential.filter((p) => !isReservedBenefitRpi(p)).length + reserved.unknown.length;
    return { show: choices.length >= 2 || (choices.length >= 1 && unknown.length + reservedCount >= 1), choices };
};

type ResidenceContext = { EstateProfile?: any; EstateAssets?: any };

/**
 * Keeps a version-2 estate's derived state consistent after any edit: clears a stale nomination. Since Slice 6 it no
 * longer rewrites the is_main_residence mirror (no consumer reads it) and never touches the properties. Returns the
 * same object for an unstamped estate and when nothing changes. Never stamps. Used by the wizard's data reducer.
 */
export const syncResidenceContext = <T extends ResidenceContext>(ctx: T): T => {
    const iht = ctx?.EstateProfile?.iht_details;
    if (!isResidenceFactsV2(iht?.residence_facts_version)) return ctx;
    const props: PropertyLike[] = ctx.EstateAssets?.properties ?? [];
    const bands = reconcileNomination(props, iht.residence_bands);
    if (bands === iht.residence_bands) return ctx;
    return { ...ctx, EstateProfile: { ...ctx.EstateProfile, iht_details: { ...iht, residence_bands: bands } } };
};

/**
 * A residence-history answer given in the Properties step (§4, §13). This is the only place the product writes
 * residence_facts_version = 2. On an unstamped estate the first answer is the deliberate transition: the stamp is
 * written, every other property's has_been_residence is reset to unanswered (a value saved before the transition
 * never becomes authoritative), and any stray nomination status is cleared. is_main_residence is never migrated into
 * the new field and is left as stored (never rewritten, §2); on the v2 estate it is never read.
 */
export const applyResidenceAnswer = <T extends ResidenceContext>(ctx: T, propertyId: string, answer: 'yes' | 'no' | 'unsure' | null): T => {
    const iht = ctx.EstateProfile?.iht_details ?? {};
    const wasV2 = isResidenceFactsV2(iht.residence_facts_version);
    const props: PropertyLike[] = ctx.EstateAssets?.properties ?? [];
    const nextProps = props.map((p) => (p.id === propertyId ? { ...p, has_been_residence: answer } : wasV2 ? p : { ...p, has_been_residence: null }));
    const nextProfile = wasV2 ? ctx.EstateProfile : {
        ...(ctx.EstateProfile ?? {}),
        iht_details: { ...iht, residence_facts_version: RESIDENCE_FACTS_V2, residence_bands: { ...(iht.residence_bands ?? {}), nomination_status: null } },
    };
    return syncResidenceContext({
        ...ctx,
        EstateProfile: nextProfile,
        EstateAssets: { ...(ctx.EstateAssets ?? {}), properties: nextProps },
    });
};

/** A nomination answer (version 2 only): a property ID, or 'undecided'. Returns the context unchanged when unstamped. */
export const applyNomination = <T extends ResidenceContext>(ctx: T, choice: string): T => {
    const iht = ctx.EstateProfile?.iht_details;
    if (!isResidenceFactsV2(iht?.residence_facts_version)) return ctx;
    const residence_bands = choice === 'undecided'
        ? { ...(iht.residence_bands ?? {}), nomination_status: 'undecided', qualifying_property_id: null }
        : { ...(iht.residence_bands ?? {}), nomination_status: 'nominated', qualifying_property_id: choice };
    return syncResidenceContext({ ...ctx, EstateProfile: { ...ctx.EstateProfile, iht_details: { ...iht, residence_bands } } });
};
